import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import ConfigPlanModal from "@components/_modals/Billing/ManagePlanModal";
import SharePlanModal from "./components/SharePlanModal";

import usePricing from "./hooks/usePricing";
import PricingView from "./view";

/**
 * Community settings → Pricing. Lists the community plans split into active /
 * archived tabs; create/edit go through ConfigPlanModal, and every saved plan
 * ends by offering its checkout link (SharePlanModal).
 */
export default function Pricing() {
  const state = usePricing();
  const [searchParams, setSearchParams] = useSearchParams();
  const REF_configPlanModal = useRef();

  const {
    communityId,
    communityLoading,
    currentCommunity,
    loading,
    applyPlanUpdate,
    syncCommunities,
    sharePlan,
    setSharePlan,
  } = state;

  function handleConfigurePlan(id) {
    if (!communityId) return;

    REF_configPlanModal.current.open(
      { id, community: communityId },
      async function (result) {
        applyPlanUpdate(result);
        await syncCommunities();
        // An archived plan has no public checkout, so there is no link to offer.
        if (result?.id && result.enabled !== false) setSharePlan(result);
      },
    );
  }

  // Deep link from the community home: `?creatingPlan=true` opens the create
  // modal once the community is resolved, then drops the param so a refresh
  // or back-navigation doesn't reopen it.
  useEffect(() => {
    if (loading || communityLoading) return;
    if (searchParams.get("creatingPlan") !== "true") return;
    if (!communityId) return;

    const next = new URLSearchParams(searchParams);
    next.delete("creatingPlan");
    setSearchParams(next, { replace: true });

    handleConfigurePlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, communityLoading]);

  return (
    <>
      <PricingView
        plans={state.plans}
        counts={state.counts}
        loading={state.loading}
        activeTab={state.activeTab}
        changeTab={state.changeTab}
        onCreate={() => handleConfigurePlan()}
        onConfigure={handleConfigurePlan}
        onShare={setSharePlan}
        onCopyLink={state.copyCheckoutLink}
        onArchive={state.archivePlan}
        onReactivate={state.reactivatePlan}
        onDelete={state.deletePlan}
      />

      <ConfigPlanModal modalRef={REF_configPlanModal} />

      <SharePlanModal
        open={!!sharePlan}
        onClose={() => setSharePlan(null)}
        plan={sharePlan}
        community={currentCommunity}
      />
    </>
  );
}
